import { api } from './client'

export const getAreas = async (locationId) => {
  const params = {}
  if (locationId) params.location_id = locationId
  const { data } = await api.get('/inventory/areas', { params })
  return data
}

export const saveArea = async (payload) => (await api.post('/inventory/areas', payload)).data

export const getStockMovements = async ({ start, end, type, sku, limit } = {}) => {
  const params = {}
  if (start) params.start = start
  if (end) params.end = end
  if (type) params.type = type
  if (sku) params.sku = sku
  if (limit) params.limit = limit
  const { data } = await api.get('/inventory/stock-movements', { params })
  return data
}

// Every product with on-hand quantity, average cost, price and barcode.
export const getInventoryItems = async () => (await api.get('/inventory/items')).data

// How many of one product are at each location: [{location_id, location_name, quantity}]
export const getStockLevels = async (productId) => (await api.get('/inventory/levels', { params: { product_id: productId } })).data

// What a write-down would cost before recording it: { segments, total_loss }
export const previewWriteDownAll = async ({ product_id, nrv }) => (await api.get('/inventory/valuation/preview-nrv', { params: { product_id, nrv } })).data
export const previewWriteDownSome = async ({ product_id, qty, nrv }) => (await api.get('/inventory/valuation/preview-ops', { params: { product_id, qty, nrv } })).data

export const createStockMovement = async (payload) => (await api.post('/inventory/stock-movements', payload)).data

export const resolveProductByCode = async (code) => {
  const { data } = await api.get('/inventory/product-by-code', { params: { code } })
  if (!data) return data
  // compute default discount on client if type/value provided
  const t = (data.discount_type || '').toLowerCase()
  const v = parseFloat(data.discount_value || 0)
  const up = parseFloat(data.unit_price || 0)
  let disc = 0
  if (t === 'percent') disc = Math.round(Math.max(0, Math.min(100, v)) * up) / 100
  if (t === 'amount') disc = Math.max(0, Math.min(v, up))
  return { ...data, discount: disc }
}

export const getSkuBins = async (code) => {
  const { data } = await api.get('/inventory/sku-bins', { params: { code } })
  return data // { product_id, sku, rows: [{location, path, qty}] }
}

// Inventory valuation write-down/shrinkage
// payload: { product_id, reason, qty?, nrv? }
export const postValuationWriteDown = async (payload) => {
  const { data } = await api.post('/inventory/valuation/write-down', payload)
  return data
}

export const listValuationApprovals = async () => {
  const { data } = await api.get('/inventory/valuation/approvals')
  return data
}

export const approveValuation = async (je_id) => {
  const { data } = await api.post('/inventory/valuation/approve', { je_id })
  return data
}

export const rejectValuation = async (je_id, reason='') => {
  const { data } = await api.post('/inventory/valuation/reject', { je_id, reason })
  return data
}

export const reverseValuation = async (payload) => {
  // payload: { product_id, new_nrv, qty?, require_approval? }
  const { data } = await api.post('/inventory/valuation/reverse', payload)
  return data
}

// Putaway queue helpers
export const enqueuePutawayForReceipt = async (receipt_id) => {
  const { data } = await api.post('/inventory/putaway-queue/from-receipt', { receipt_id })
  return data
}
export const listPutawayQueue = async () => {
  const { data } = await api.get('/inventory/putaway-queue', { params: { status: 'Open' } })
  return data
}
export const markPutawayDone = async (id) => {
  const { data } = await api.post('/inventory/putaway-queue/done', { id })
  return data
}

// Pick location mapping
export const getPickLocation = async ({ product_id, code } = {}) => {
  const params = {}
  if (product_id) params.product_id = product_id
  if (code) params.code = code
  const { data } = await api.get('/inventory/pick-location', { params })
  return data
}

export const setPickLocation = async ({ product_id, area_id }) => {
  const { data } = await api.post('/inventory/pick-location', { product_id, area_id })
  return data
}

export const listPickLocations = async (productIds = []) => {
  const params = {}
  if (productIds && productIds.length) params.product_ids = productIds.join(',')
  const { data } = await api.get('/inventory/pick-locations', { params })
  return data
}

export const addPickLocation = async ({ product_id, area_id, is_primary = false, sort_order = 0 }) => {
  const { data } = await api.post('/inventory/pick-locations', { product_id, area_id, is_primary, sort_order })
  return data
}

export const updatePickLocation = async ({ product_id, area_id, is_primary, sort_order }) => {
  const body = { product_id, area_id }
  if (typeof is_primary !== 'undefined') body.is_primary = is_primary
  if (typeof sort_order !== 'undefined') body.sort_order = sort_order
  const { data } = await api.patch('/inventory/pick-locations', body)
  return data
}

export const deletePickLocation = async ({ product_id, area_id }) => {
  const { data } = await api.delete('/inventory/pick-locations', { data: { product_id, area_id } })
  return data
}

// Cost layers utilities
export const seedCostLayers = async ({ force = false, location_id = null } = {}) => {
  const body = {}
  if (force) body.force = true
  if (location_id != null) body.location_id = location_id
  const { data } = await api.post('/inventory/layers/seed', body)
  return data // { inserted }
}

export const listCostLayers = async ({ product_id, code, location_id, limit } = {}) => {
  const params = {}
  if (product_id) params.product_id = product_id
  if (code) params.code = code
  if (location_id) params.location_id = location_id
  if (limit) params.limit = limit
  const { data } = await api.get('/inventory/layers', { params })
  return data
}

export const listCostLayerSummary = async ({ product_id, code, location_id, group = 'product', limit, include_nrv = false } = {}) => {
  const params = {}
  if (product_id) params.product_id = product_id
  if (code) params.code = code
  if (location_id) params.location_id = location_id
  if (group) params.group = group
  if (limit) params.limit = limit
  if (include_nrv) params.include_nrv = 1
  const { data } = await api.get('/inventory/layers/summary', { params })
  return data
}

// What is in one bin: { area: {area_id, path, name, location_name}, rows: [{product_id, sku, name, qty}] }
export const getBinContents = async (areaId) => (await api.get('/inventory/bin-contents', { params: { area_id: areaId } })).data

// Stock counts (counted with phones or here; see backend stock_counts.py).
export const listCounts = async (status) => (await api.get('/inventory/counts', { params: status ? { status } : {} })).data
export const createCount = async ({ location_id, label } = {}) => (await api.post('/inventory/counts', { location_id, label })).data
// { ...count, bin_tracking, lines, people, results? (once finished), journal_entries? }
export const getCount = async (id) => (await api.get(`/inventory/counts/${id}`)).data
// scans: [{ scan_id, product_id, area_id?, delta } | { scan_id, product_id, area_id?, remove: true }]
export const addCountScans = async (id, scans) => (await api.post(`/inventory/counts/${id}/scans`, { scans })).data
// Counted against expected: { lines, missing, totals, needs_approval, approval_threshold, can_approve }
export const reviewCount = async (id, countMissing) => (await api.get(`/inventory/counts/${id}/review`, { params: { count_missing: countMissing ? 1 : 0 } })).data
export const finishCount = async (id, { count_missing, require_approval }) => (await api.post(`/inventory/counts/${id}/finish`, { count_missing, require_approval })).data
export const approveCount = async (id) => (await api.post(`/inventory/counts/${id}/approve`, {})).data
export const sendBackCount = async (id, note) => (await api.post(`/inventory/counts/${id}/send-back`, { note })).data
export const cancelCount = async (id) => (await api.delete(`/inventory/counts/${id}`)).data
